import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Input, Select, Segmented, Empty, Spin, Tag } from 'antd';
import {
  ClockCircleOutlined, FolderOutlined, RightOutlined, SearchOutlined, UploadOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';
import {
  fetchReadyToSignFormsApi, fetchSignableDocumentsApi, fetchSignableFoldersApi, getDocumentDetail,
} from '../../service/matter';
import { createAssemblyJob, pollAssemblyJob } from '../../service/assemblyService';

const SOURCE_LABELS = {
  boxes: 'Signature boxes set up',
  tags: 'Signature spots in the document',
  signature_fields: 'Fillable signature field',
};

const PDF_TYPES = new Set(['pdf', 'application/pdf']);
const WORD_TYPES = new Set([
  'docx',
  'doc',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]);

const kindOfType = (value) => {
  const type = String(value || '').trim().toLowerCase();
  if (PDF_TYPES.has(type)) return 'pdf';
  if (WORD_TYPES.has(type)) return 'word';
  return '';
};

// 'pdf', 'word' or '' — file types are stored as extensions or MIME types.
export const signableKindOf = (doc) => {
  if (!doc) return '';
  if (doc.signable_kind) return doc.signable_kind;
  const name = String(doc.name || '');
  return kindOfType(doc.file_type) || (name.includes('.') ? kindOfType(name.split('.').pop()) : '');
};

const KIND_LABELS = { pdf: 'PDF', word: 'Word' };

// Where the one-off document list is looking: the matter's top level, its
// newest documents, or a folder id.
const TOP = '';
const RECENT = 'recent';

const folderIdOf = (doc) => {
  const folder = doc?.folder_id ?? doc?.folder;
  if (folder && typeof folder === 'object') return folder.id ? String(folder.id) : '';
  return folder ? String(folder) : '';
};

const countLabel = (count) => `${count} document${count === 1 ? '' : 's'}`;

const Intro = styled.p`
  color: #4b5563;
  margin: 12px 0;
`;

const Toolbar = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
`;

const List = styled.div`
  max-height: 46vh;
  overflow-y: auto;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
`;

const Row = styled.button`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  text-align: left;
  padding: 10px 14px;
  border: 0;
  border-bottom: 1px solid #f3f4f6;
  cursor: pointer;
  background: ${({ $selected }) => ($selected ? '#eff6ff' : '#fff')};
  box-shadow: ${({ $selected }) => ($selected ? 'inset 3px 0 0 #1d4ed8' : 'none')};
  &:hover { background: ${({ $selected }) => ($selected ? '#eff6ff' : '#f9fafb')}; }
`;

const Name = styled.div`
  font-weight: 500;
  color: #111827;
`;

const Meta = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

const RowLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const RowIcon = styled.span`
  color: ${({ $muted }) => ($muted ? '#6b7280' : '#ca8a04')};
  font-size: 18px;
`;

const Crumbs = styled.nav`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  margin-bottom: 8px;
  font-size: 13px;
  color: #6b7280;
`;

const Crumb = styled.button`
  border: 0;
  background: none;
  padding: 2px 4px;
  border-radius: 4px;
  cursor: pointer;
  color: ${({ $current }) => ($current ? '#111827' : '#1d4ed8')};
  font-weight: ${({ $current }) => ($current ? 600 : 400)};
  &:hover { background: ${({ $current }) => ($current ? 'none' : '#eff6ff')}; }
`;

const Status = styled.div`
  margin-top: 12px;
  color: ${({ $error }) => ($error ? '#b91c1c' : '#1d4ed8')};
`;

const SendForSignatureModal = ({
  open, matterId, refreshKey, onClose, onChooseForm, onChooseDocument, onUploadRequest,
}) => {
  const [tab, setTab] = useState('forms');
  const [forms, setForms] = useState([]);
  const [formsLoading, setFormsLoading] = useState(false);
  const [formSearch, setFormSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [selectedForm, setSelectedForm] = useState(null);
  const [docSearch, setDocSearch] = useState('');
  const [documents, setDocuments] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [moreDocuments, setMoreDocuments] = useState(false);
  const [folderTree, setFolderTree] = useState(null);
  const [treeStatus, setTreeStatus] = useState('loading');
  const [location, setLocation] = useState(TOP);
  const [status, setStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const inFlightRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    setSelectedForm(null);
    setSelectedDoc(null);
    setLocation(TOP);
    setStatus(null);
    setSubmitting(false);
    inFlightRef.current = false;
    setFormsLoading(true);
    fetchReadyToSignFormsApi()
      .then((res) => setForms(res?.data?.results || []))
      .catch(() => setForms([]))
      .finally(() => setFormsLoading(false));
  }, [open]);

  useEffect(() => {
    if (!open || tab !== 'oneoff' || !matterId) return undefined;
    let cancelled = false;
    setTreeStatus('loading');
    fetchSignableFoldersApi(matterId)
      .then((res) => {
        if (cancelled) return;
        setFolderTree(res?.data || null);
        setTreeStatus('ready');
      })
      .catch(() => {
        // Without folders the window still works as the plain newest-first list.
        if (cancelled) return;
        setFolderTree(null);
        setTreeStatus('failed');
      });
    return () => {
      cancelled = true;
    };
  }, [open, tab, matterId, refreshKey]);

  const foldersById = useMemo(
    () => new Map((folderTree?.folders || []).map((folder) => [folder.id, folder])),
    [folderTree]
  );
  const searching = Boolean(docSearch.trim());
  const browsing = !searching && foldersById.size > 0;
  // A folder that no longer holds anything to sign (after an upload, say) falls back to the top.
  const place = location && location !== RECENT && !foldersById.has(location) ? TOP : location;

  const subfolders = useMemo(() => {
    if (!browsing || place === RECENT) return [];
    const parent = place || null;
    return (folderTree?.folders || []).filter((folder) => (folder.parent_id || null) === parent);
  }, [browsing, place, folderTree]);

  // A document chosen in one folder must not stay chosen, unseen, after moving to another.
  const openPlace = (next) => {
    setSelectedDoc(null);
    setLocation(next);
  };

  const folderPath = (folderId) => {
    const path = [];
    const seen = new Set();
    let cursor = foldersById.get(folderId);
    while (cursor && !seen.has(cursor.id)) {
      seen.add(cursor.id);
      path.unshift(cursor);
      cursor = cursor.parent_id ? foldersById.get(cursor.parent_id) : null;
    }
    return path;
  };

  let docRequest = null;
  if (open && tab === 'oneoff' && matterId && treeStatus !== 'loading') {
    if (searching) docRequest = { search: docSearch.trim() };
    else if (!browsing || place === RECENT) docRequest = {};
    else if (place) docRequest = { folder: place };
    else if (folderTree?.root_document_count) docRequest = { folder: 'root' };
  }
  const docRequestKey = docRequest ? JSON.stringify(docRequest) : '';

  useEffect(() => {
    if (!docRequestKey) {
      setDocuments([]);
      setMoreDocuments(false);
      setDocsLoading(false);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      setDocsLoading(true);
      fetchSignableDocumentsApi({ matterId, ...JSON.parse(docRequestKey) })
        .then((res) => {
          if (cancelled) return;
          setDocuments((res?.data?.results || []).filter((doc) => signableKindOf(doc)));
          setMoreDocuments(Boolean(res?.data?.truncated));
        })
        .catch(() => {
          if (cancelled) return;
          setDocuments([]);
          setMoreDocuments(false);
        })
        .finally(() => !cancelled && setDocsLoading(false));
    }, searching ? 250 : 0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [docRequestKey, matterId, refreshKey, searching]);

  const categories = useMemo(
    () => Array.from(new Set(forms.map((form) => form.category_name).filter(Boolean))).sort(),
    [forms]
  );

  const visibleForms = useMemo(() => {
    const query = formSearch.trim().toLowerCase();
    return forms.filter((form) =>
      (category === 'all' || form.category_name === category)
      && (!query || form.name.toLowerCase().includes(query)));
  }, [forms, formSearch, category]);

  const busy = submitting;

  // Guards double clicks: the parent closes this window once a choice lands.
  const claim = () => {
    if (inFlightRef.current) return false;
    inFlightRef.current = true;
    setSubmitting(true);
    return true;
  };

  const release = () => {
    inFlightRef.current = false;
    setSubmitting(false);
  };

  const chooseForm = (form) => {
    if (form && claim()) onChooseForm(form);
  };

  const handleNext = async () => {
    if (tab === 'forms') {
      chooseForm(selectedForm);
      return;
    }
    if (!selectedDoc || !claim()) return;
    if (signableKindOf(selectedDoc) !== 'word') {
      onChooseDocument(selectedDoc, { placeBoxes: true });
      return;
    }
    try {
      setStatus({ text: 'Making a PDF copy of the Word document…' });
      const stem = String(selectedDoc.name || 'Document').replace(/\.[^.]+$/, '');
      const job = await createAssemblyJob({
        matter: matterId,
        outputName: `${stem} (for signature).pdf`,
        folder: selectedDoc.folder || selectedDoc.folder_id || undefined,
        items: [{ document_id: selectedDoc.id }],
      });
      const finished = await pollAssemblyJob(job.job_id, { intervalMs: 1200, timeoutMs: 3 * 60 * 1000 });
      if (finished.status !== 'completed' || !finished.output_document_id) {
        throw new Error(finished.error_detail || 'The PDF copy could not be made.');
      }
      const detail = await getDocumentDetail(finished.output_document_id);
      setStatus(null);
      onChooseDocument(detail?.data || { id: finished.output_document_id, name: `${stem} (for signature).pdf` }, { placeBoxes: true });
    } catch (err) {
      setStatus({ text: err?.message || 'The PDF copy could not be made. Try again.', error: true });
      release();
    }
  };

  const nextLabel = tab === 'forms'
    ? 'Fill in & send'
    : signableKindOf(selectedDoc) === 'word' ? 'Make PDF & place boxes' : 'Place boxes';

  return (
    <Modal
      open={open}
      onCancel={busy ? undefined : onClose}
      title="Send a Document for E-Signature"
      width={760}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onClose} disabled={busy}>Cancel</Button>,
        <Button
          key="next"
          type="primary"
          loading={busy}
          disabled={tab === 'forms' ? !selectedForm : !selectedDoc}
          onClick={handleNext}
          data-testid="esign-launcher-next"
        >
          {nextLabel}
        </Button>,
      ]}
    >
      <Segmented
        block
        value={tab}
        onChange={setTab}
        disabled={busy}
        options={[
          { label: `Ready-to-sign forms (${forms.length})`, value: 'forms' },
          { label: 'Place boxes yourself', value: 'oneoff' },
        ]}
      />

      {tab === 'forms' && (
        <>
          <Intro>
            These forms already know where everyone signs. Pick one, check the client details it fills in,
            and it opens ready to send.
          </Intro>
          <Toolbar>
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Search forms"
              value={formSearch}
              onChange={(e) => setFormSearch(e.target.value)}
            />
            <Select
              style={{ minWidth: 200 }}
              value={category}
              onChange={setCategory}
              options={[{ value: 'all', label: 'All categories' }, ...categories.map((c) => ({ value: c, label: c }))]}
            />
          </Toolbar>
          <List data-testid="esign-ready-forms">
            {formsLoading && <div style={{ padding: 24, textAlign: 'center' }}><Spin /></div>}
            {!formsLoading && visibleForms.length === 0 && (
              <Empty
                style={{ padding: 24 }}
                description={forms.length
                  ? 'No forms match your search.'
                  : 'No forms are ready to sign yet. Signature boxes can be set up on a PDF template in Settings → File Templates, and Word templates need Formstack signature tags.'}
              />
            )}
            {!formsLoading && visibleForms.map((form) => (
              <Row
                key={form.id}
                type="button"
                $selected={selectedForm?.id === form.id}
                disabled={busy}
                onClick={() => setSelectedForm(form)}
                onDoubleClick={() => chooseForm(form)}
              >
                <div>
                  <Name>{form.name}</Name>
                  <Meta>{form.category_name} · {SOURCE_LABELS[form.signature?.source] || 'Ready to sign'}</Meta>
                </div>
                <div>
                  {(form.signature?.signers || []).map((signer) => (
                    <Tag key={signer} color="blue">{signer}</Tag>
                  ))}
                </div>
              </Row>
            ))}
          </List>
        </>
      )}

      {tab === 'oneoff' && (
        <>
          <Intro>
            Choose any PDF or Word document in this matter: open its folders, or search by name. You&apos;ll drag signature, initials, date and name
            boxes onto it and choose who signs each one. Word documents get a PDF copy first, so the client
            signs exactly what you see.
          </Intro>
          <Toolbar>
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Search this matter's documents"
              value={docSearch}
              onChange={(e) => setDocSearch(e.target.value)}
            />
            {onUploadRequest && (
              <Button icon={<UploadOutlined />} onClick={onUploadRequest} disabled={busy}>
                Upload a file
              </Button>
            )}
          </Toolbar>
          {browsing && place !== TOP && (
            <Crumbs aria-label="Folder" data-testid="esign-oneoff-crumbs">
              <Crumb type="button" onClick={() => openPlace(TOP)}>All folders</Crumb>
              {(place === RECENT ? [{ id: RECENT, name: 'Recently added' }] : folderPath(place)).map((folder, index, all) => (
                <React.Fragment key={folder.id}>
                  <RightOutlined style={{ fontSize: 10 }} />
                  <Crumb
                    type="button"
                    $current={index === all.length - 1}
                    onClick={() => openPlace(folder.id)}
                  >
                    {folder.name}
                  </Crumb>
                </React.Fragment>
              ))}
            </Crumbs>
          )}
          <List data-testid="esign-oneoff-documents">
            {browsing && place === TOP && (
              <Row type="button" disabled={busy} onClick={() => openPlace(RECENT)} data-testid="esign-oneoff-recent">
                <RowLabel>
                  <RowIcon $muted><ClockCircleOutlined /></RowIcon>
                  <div>
                    <Name>Recently added</Name>
                    <Meta>The newest PDF and Word documents in this matter</Meta>
                  </div>
                </RowLabel>
                <RightOutlined style={{ color: '#9ca3af' }} />
              </Row>
            )}
            {subfolders.map((folder) => (
              <Row
                key={folder.id}
                type="button"
                disabled={busy}
                onClick={() => openPlace(folder.id)}
                data-testid={`esign-folder-${folder.name}`}
              >
                <RowLabel>
                  <RowIcon><FolderOutlined /></RowIcon>
                  <div>
                    <Name>{folder.name}</Name>
                    <Meta>{countLabel(folder.document_count)}</Meta>
                  </div>
                </RowLabel>
                <RightOutlined style={{ color: '#9ca3af' }} />
              </Row>
            ))}
            {(docsLoading || treeStatus === 'loading') && (
              <div style={{ padding: 24, textAlign: 'center' }}><Spin /></div>
            )}
            {!docsLoading && treeStatus !== 'loading' && documents.length === 0 && subfolders.length === 0
              && !(browsing && place === TOP) && (
              <Empty
                style={{ padding: 24 }}
                description={searching
                  ? 'No PDF or Word documents in this matter match your search.'
                  : 'No PDF or Word documents found in this matter.'}
              />
            )}
            {!docsLoading && documents.map((doc) => {
              const where = searching || place === RECENT
                ? folderPath(folderIdOf(doc)).map((folder) => folder.name).join(' › ')
                : '';
              return (
                <Row
                  key={doc.id}
                  type="button"
                  $selected={selectedDoc?.id === doc.id}
                  disabled={busy}
                  onClick={() => setSelectedDoc(doc)}
                >
                  <div>
                    <Name>{doc.name}</Name>
                    <Meta>
                      {KIND_LABELS[signableKindOf(doc)]}
                      {doc.added_on || doc.time_created ? ` · added ${new Date(doc.added_on || doc.time_created).toLocaleDateString()}` : ''}
                      {where ? ` · in ${where}` : ''}
                    </Meta>
                  </div>
                  {signableKindOf(doc) === 'word' && <Tag>PDF copy made first</Tag>}
                </Row>
              );
            })}
          </List>
          {!docsLoading && moreDocuments && (
            <Meta style={{ marginTop: 8 }} data-testid="esign-oneoff-more">
              {browsing && place && place !== RECENT
                ? `Showing the first ${documents.length} in this folder. Search by name to find the others.`
                : `Showing the newest ${documents.length}. Search by name to find older documents.`}
            </Meta>
          )}
          {status && <Status $error={status.error} data-testid="esign-launcher-status">{status.text}</Status>}
        </>
      )}
    </Modal>
  );
};

SendForSignatureModal.propTypes = {
  open: PropTypes.bool.isRequired,
  matterId: PropTypes.string,
  refreshKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClose: PropTypes.func.isRequired,
  onChooseForm: PropTypes.func.isRequired,
  onChooseDocument: PropTypes.func.isRequired,
  onUploadRequest: PropTypes.func,
};

SendForSignatureModal.defaultProps = {
  matterId: '',
  refreshKey: '',
  onUploadRequest: null,
};

export default SendForSignatureModal;
